/**
 * columns — the dynamic table column sets from Angular
 * `DEEComponent.valueBasedOnClmChange()`.
 *
 * Returns every column array the detail tabs use, chosen by CounsellingType,
 * isScert, schType (aided), Directorate and office (IsOfc). Ported verbatim so
 * the columns shown per counselling type match Angular exactly.
 */

import { CounsellingRecord, RoleContext } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface ColumnDef {
  field: string;
  header: string;
}

export interface ColumnSets {
  teacher: ColumnDef[];
  vacancies: ColumnDef[];
  confirmation: ColumnDef[];
  notWilling: ColumnDef[];
  absent: ColumnDef[];
  final: ColumnDef[];
  /** Angular: IsOfc — office-based (SclCategory includes '6'). */
  isOfc: boolean;
}

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

// ── Default column sets (Angular constructor) ───────────────────────────────

const DEFAULT_TEACHER: ColumnDef[] = [
  { field: 'seniority_state', header: 'Seniority No.' },
  { field: 'teacher_id', header: 'Teacher Id' },
  { field: 'name', header: 'Name' },
  { field: 'subjectLabel', header: 'Subject' },
  { field: 'priority', header: 'Priority' },
  { field: 'school_name', header: 'School Name' },
];

const DEFAULT_VACANCIES: ColumnDef[] = [
  { field: 'district_name', header: 'District' },
  { field: 'block2', header: 'Block' },
  { field: 'udise_code', header: 'UDISE ID' },
  { field: 'type', header: 'School Type' },
  { field: 'school_name', header: 'School Name' },
  { field: 'subjectLabel', header: 'Subject' },
  { field: 'school_type', header: 'School Management' },
  { field: 'vac', header: 'Vacancy Count' },
];

const DEFAULT_CONFIRMATION: ColumnDef[] = [
  { field: 'name', header: 'Name' },
  { field: 'old_udise_code', header: 'Old UDISE ID' },
  { field: 'new_udise_code', header: 'New UDISE ID' },
  { field: 'old_sch_name', header: 'Old School Name' },
  { field: 'new_sch_name', header: 'New School Name' },
];

const DEFAULT_STATUS_LIST: ColumnDef[] = [
  { field: 'seniority_state', header: 'Seniority No.' },
  { field: 'teacher_id', header: 'Teacher ID' },
  { field: 'name', header: 'Name' },
  { field: 'udise_code', header: 'UDISE ID' },
  { field: 'school_name', header: 'School Name' },
];

const DEFAULT_FINAL: ColumnDef[] = [
  { field: 'seniority_state', header: 'Seniority No.' },
  { field: 'teacher_id', header: 'Teacher ID' },
  { field: 'name', header: 'Name' },
  { field: 'Designation', header: 'Old Designation' },
  { field: 'NewDesign', header: 'New Designation' },
  { field: 'school_name', header: 'Old School Name' },
  { field: 'newSchlNme', header: 'New School Name' },
];

/**
 * Builds the full column set for a started counselling row (StartEndRow) + role.
 * Mirrors `valueBasedOnClmChange` including its cascading overrides
 * (CounsellingType → isScert → schType='2').
 */
export function buildColumnSets(
  startEndRow: CounsellingRecord | null,
  role: RoleContext
): ColumnSets {
  const type = str(startEndRow?.CounsellingType);
  const directorate = str(startEndRow?.Directorate);
  const schType = str(startEndRow?.schType);
  const sclCategory = startEndRow?.SclCategory;
  const { isScert } = role;

  let teacher = [...DEFAULT_TEACHER];
  let vacancies = [...DEFAULT_VACANCIES];
  let confirmation = [...DEFAULT_CONFIRMATION];
  let notWilling = [...DEFAULT_STATUS_LIST];
  let absent = [...DEFAULT_STATUS_LIST];
  let final = [...DEFAULT_FINAL];
  let isOfc = false;

  if (type === '3') {
    teacher = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'teacher_id', header: 'Teacher Id' },
      { field: 'name', header: 'Name' },
      { field: 'desigLabel', header: 'Designation' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'school_name', header: 'School Name' },
    ];
    vacancies = [
      { field: 'district_name', header: 'District' },
      { field: 'block2', header: 'Block' },
      { field: 'udise_code', header: 'UDISE ID' },
      { field: 'type', header: 'School Type' },
      { field: 'school_name', header: 'School Name' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'school_type', header: 'School Management' },
      { field: 'vac', header: 'Vacancy Count' },
    ];
  } else if (type === '1') {
    vacancies = [
      { field: 'district_name', header: 'District' },
      { field: 'block2', header: 'Block' },
      { field: 'udise_code', header: 'UDISE ID' },
      { field: 'type', header: 'School Type' },
      { field: 'school_name', header: 'School Name' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'school_type', header: 'School Management' },
      { field: 'vac', header: 'Vacancy Count' },
      { field: 'need', header: 'Need Count' },
    ];
    teacher = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'teacher_id', header: 'Teacher Id' },
      { field: 'name', header: 'Name' },
      { field: 'desigLabel', header: 'Designation' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'priority', header: 'Priority' },
      { field: 'school_name', header: 'School Name' },
    ];
  } else if (type === '2') {
    if (sclCategory && String(sclCategory).includes('6')) {
      isOfc = true;
      teacher = [
        { field: 'seniority_state', header: 'Seniority No.' },
        { field: 'teacher_id', header: 'Teacher Id' },
        { field: 'name', header: 'Name' },
        { field: 'subjectLabel', header: 'Subject' },
        { field: 'priority', header: 'Priority' },
        { field: 'DistrictNme', header: 'District' },
        { field: 'ofcNme', header: 'Office Name' },
      ];
      final = [
        { field: 'seniority_state', header: 'Seniority No.' },
        { field: 'teacher_id', header: 'Teacher ID' },
        { field: 'name', header: 'Name' },
        { field: 'Designation', header: 'Old Designation' },
        { field: 'NewDesign', header: 'New Designation' },
        { field: 'ofcNme', header: 'Office Name' },
        { field: 'newSchlNme2', header: 'New Office Name' },
      ];
      notWilling = [
        { field: 'seniority_state', header: 'Seniority No.' },
        { field: 'teacher_id', header: 'Teacher ID' },
        { field: 'name', header: 'Name' },
        { field: 'ofcNme', header: 'Office Name' },
      ];
      absent = [
        { field: 'seniority_state', header: 'Seniority No.' },
        { field: 'teacher_id', header: 'Teacher ID' },
        { field: 'name', header: 'Name' },
        { field: 'ofcNme', header: 'Office Name' },
      ];
    } else {
      teacher = [
        { field: 'seniority_state', header: 'Seniority No.' },
        { field: 'teacher_id', header: 'Teacher Id' },
        { field: 'name', header: 'Name' },
        { field: 'desigLabel', header: 'Designation' },
        { field: 'subjectLabel', header: 'Subject' },
        { field: 'priority', header: 'Priority' },
        { field: 'school_name', header: 'School Name' },
      ];
    }
    vacancies = [
      { field: 'district_name', header: 'District' },
      { field: 'block2', header: 'Block' },
      { field: 'udise_code', header: 'UDISE ID' },
      { field: 'type', header: 'School Type' },
      { field: 'school_name', header: 'School Name' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'school_type', header: 'School Management' },
      { field: 'vac', header: 'Vacancy Count' },
    ];
    if (directorate === '6') {
      vacancies = [
        { field: 'district_name', header: 'District' },
        { field: 'block2', header: 'Block' },
        { field: 'udise_code', header: 'UDISE ID' },
        { field: 'type', header: 'School Type' },
        { field: 'school_name', header: 'School Name' },
        { field: 'DesignationLabel', header: 'Designation' },
        { field: 'subjectLabel', header: 'Subject' },
        { field: 'school_type', header: 'School Management' },
        { field: 'vac', header: 'Vacancy Count' },
      ];
      teacher = [
        { field: 'seniority_state', header: 'Seniority No.' },
        { field: 'teacher_id', header: 'Teacher Id' },
        { field: 'name', header: 'Name' },
        { field: 'desigLabel', header: 'Designation' },
        { field: 'subjectLabel', header: 'Subject' },
        { field: 'priority', header: 'Priority' },
        { field: 'special_priority_label', header: 'Special Priority' },
        { field: 'school_name', header: 'School Name' },
      ];
    }
  } else if (type === '4') {
    teacher = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'application_id', header: 'Application Id' },
      { field: 'name', header: 'Name' },
      { field: 'desigLabel', header: 'Designation' },
      { field: 'DistrictName', header: 'District' },
    ];
    confirmation = [
      { field: 'name', header: 'Name' },
      { field: 'new_udise_code', header: 'UDISE CODE' },
      { field: 'new_sch_name', header: 'New School Name' },
    ];
    notWilling = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'application_id', header: 'Application Id' },
      { field: 'name', header: 'Name' },
    ];
    absent = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'application_id', header: 'Application Id' },
      { field: 'name', header: 'Name' },
    ];
    final = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'application_id', header: 'Application Id' },
      { field: 'name', header: 'Name' },
      { field: 'NewDesign', header: 'Designation' },
      { field: 'newSchlNme', header: 'New School Name' },
    ];
  }

  // isScert overrides (Angular: applied after the type block)
  if (isScert) {
    teacher = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'teacher_id', header: 'Teacher Id' },
      { field: 'name', header: 'Name' },
      { field: 'desigLabel', header: 'Designation' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'ofcNme', header: 'Office Name' },
    ];
    vacancies = [
      { field: 'district_name', header: 'District' },
      { field: 'type', header: 'School Type' },
      { field: 'school_name', header: 'School Name' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'vac', header: 'Vacancy Count' },
    ];
    confirmation = [
      { field: 'name', header: 'Name' },
      { field: 'old_sch_name', header: 'Old Office Name' },
      { field: 'new_sch_name', header: 'New Office Name' },
    ];
    notWilling = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'teacher_id', header: 'Teacher ID' },
      { field: 'name', header: 'Name' },
      { field: 'ofcNme', header: 'Office Name' },
    ];
    absent = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'teacher_id', header: 'Teacher ID' },
      { field: 'name', header: 'Name' },
      { field: 'ofcNme', header: 'Office Name' },
    ];
  }

  // schType === '2' (aided) overrides (Angular: applied last)
  if (schType === '2') {
    teacher = [
      { field: 'seniority_state', header: 'Seniority No.' },
      { field: 'teacher_id', header: 'Teacher Id' },
      { field: 'name', header: 'Name' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'school_name', header: 'School Name' },
      { field: 'minority', header: 'Minority Type' },
    ];
    vacancies = [
      { field: 'district_name', header: 'District' },
      { field: 'udise_code', header: 'UDISE ID' },
      { field: 'type', header: 'School Type' },
      { field: 'school_name', header: 'School Name' },
      { field: 'DesignationLabel', header: 'Designation' },
      { field: 'subjectLabel', header: 'Subject' },
      { field: 'school_type', header: 'School Management' },
      { field: 'minority', header: 'Minority' },
      { field: 'vac', header: 'Vacancy Count' },
    ];
  }

  return { teacher, vacancies, confirmation, notWilling, absent, final, isOfc };
}

/* eslint-enable @typescript-eslint/no-explicit-any */
